// ─────────────────────────────────────────────────────────────────────────────
// DEAL_STAGES — single source of truth for the owner deal lifecycle
// ─────────────────────────────────────────────────────────────────────────────
// The forward funnel starts when an inbound seller lead is captured.
// "Passed" is a terminal off-ramp (owner declined, no deal).
// ─────────────────────────────────────────────────────────────────────────────

export const DEAL_STAGES = [
  // The funnel starts at title work. "Call In" used to sit in front of
  // it, holding unqualified leads until somebody promoted them — that
  // is the Lead Center's job now, and it does it better: a lead there
  // has call history, outcomes, skip-trace state and a section
  // assignment, none of which a kanban column ever held. A lead only
  // becomes a deal when we have decided to work it, and the first work
  // is title.
  //
  // Title research happens before pricing now — agents need a clean
  // chain-of-title before they can quote a number with confidence.
  // Was: Pricing → Preliminary Title; flipped per user request.
  'Preliminary Title',
  'Pricing',
  'Offer Made',
  'Under Contract',
  // "Processing" = the deal is actively being worked to close.
  // Renamed from "Disposition": buyers are chosen before the deal
  // goes under contract now, so the old buyer-driven disposition
  // stage became a plain working-to-close status.
  'Processing',
  'Closed',
  'Funded',
  'Passed',
];

// Forward funnel only (no terminal Passed) — used for lifecycle checklists.
export const LIFECYCLE_STAGES = DEAL_STAGES.filter((s) => s !== 'Passed');

// Stages where the deal is read-only. Once a deal is Closed or the
// deal is funded, the contractual + financial state is
// settled and inline edits / new offers / mutating modal saves should
// be blocked so a stray click can't rewrite history. Stage drag-out
// (to fix a wrong stage) and outright delete remain available — those
// are the escape hatches.
export const DEAL_LOCKED_STAGES = new Set(['Closed', 'Funded']);

// Stages drawn on the main map's Current Deals layer.
//
// Closed and Funded are included: where we have already bought is the
// most useful thing to see when working an area. Passed is excluded —
// a deal we declined is not a position we hold. Preliminary Title and
// the stages upstream of Pricing stay off because they have no
// confirmed tracts yet.
//
// Processing was hidden here and is not any more, asked for directly.
// It sits between Under Contract and Closed — the deal is signed and
// working its way to funding — so the tracts are as confirmed as the
// ones on either side of it, and leaving it out drew a gap in the
// middle of the pipeline: an area could look untouched while a deal on
// it was days from closing.
//
// The map builds this layer twice — server-side in
// api/_lib/current-deal-map.js and browser-side in MapboxMapPage as a
// fallback when that endpoint fails. Both read this set, so the map
// cannot show a different set of deals depending on which path ran.
export const CURRENT_DEAL_MAP_STAGES = new Set([
  'Pricing',
  'Offer Made',
  'Under Contract',
  'Processing',
  'Closed',
  'Funded',
]);

export function isDealLocked(deal, user) {
  if (!deal) return false;
  // Admins can edit any deal, Closed and Funded included — the lock
  // protects settled deals from stray landman clicks, not from the
  // admin correcting history on purpose.
  if (user?.role === 'admin') return false;
  return DEAL_LOCKED_STAGES.has(normalizeStage(deal.stage));
}

// Stage → accent color. Used for kanban column top-borders, badge chips,
// lifecycle pills, and the coloured left-border on dashboard cards.
// One hue, deepening as the deal advances, rather than a different colour
// of the spectrum per stage. A rainbow reads as a progress bar in a game;
// a ramp reads as a deal getting closer to money, and it says the same
// thing at a glance without nine hues to learn.
//
// The exceptions carry real meaning rather than position: green for the
// two stages where money has actually moved, grey for the one where the
// deal stopped.
export const STAGE_COLORS = {
  // In funnel order, lightest first. These two were the other way
  // round: the stages were flipped to put title before pricing and the
  // colours were not, so the ramp lightened in the middle. With Call In
  // gone, Preliminary Title is the entry stage and takes the entry tone.
  'Preliminary Title': '#5f7ea3',
  'Pricing':           '#456d9c',
  'Offer Made':        '#2f5c93',
  'Under Contract':    '#1e4a83',
  'Processing':        '#163d70', // deepest — the last step before money
  'Closed':            '#17795f', // money realised
  'Funded':            '#0f5c48',
  'Passed':            '#6b7280', // stopped
};

// The same ramp, lifted to survive a dark ground.
//
// The palette above was drawn for light surfaces — white cards, the
// Deal Tracker's column headers — and it works there. On the navy
// blueprint paper (#001d4a) the deep end of it disappears: Processing
// measures 1.52:1 against that board and Under Contract 1.85:1, while
// the grey an UNREACHED stage is drawn in composites to 1.43:1. So the
// two stages closest to money were marked almost exactly as faintly as
// the ones the deal had not got to, on the screen whose whole job is
// saying where the deal is.
//
// Every colour is mixed toward white by the SAME fraction. That matters:
// one shared fraction is a monotone map, so the ramp's order comes
// through untouched — Preliminary Title still reads lighter than
// Processing, and the greens stay apart from the blues. Lifting each
// colour by whatever it individually needed would have flattened the
// ramp into a row of near-identical mid-blues, which is the thing the
// ramp exists to avoid.
//
// 0.34 is the smallest shared fraction that puts every stage at or past
// 4:1 against the board. Checked by scripts/test-stage-color-on-dark.mjs
// rather than by eye, because this is a contrast floor and eyes are
// exactly what it is there to not depend on.
const ON_DARK_LIFT = 0.34;

/** Mix a hex colour toward white by `t` (0 = unchanged, 1 = white). */
function lightenToward(hex, t) {
  const raw = String(hex || '').trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  const channel = (i) => {
    const c = parseInt(raw.slice(i, i + 2), 16);
    return Math.round(c + (255 - c) * t).toString(16).padStart(2, '0');
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

export const STAGE_COLORS_ON_DARK = Object.fromEntries(
  Object.entries(STAGE_COLORS).map(([stage, hex]) => [stage, lightenToward(hex, ON_DARK_LIFT)]),
);

/**
 * A stage's colour for drawing ON the navy board.
 *
 * Use this anywhere the mark sits directly on the blueprint paper — a
 * progress fill, a rail segment. Washes that composite the colour at
 * low alpha over the board keep STAGE_COLORS: they are a tint of the
 * ground, not a mark against it.
 */
export function stageColorOnDark(stage) {
  // normalizeStage only ever returns a member of DEAL_STAGES, so this
  // falls through only if a stage is added there without a colour. The
  // grey is for that case and nothing else; the test asserts the map is
  // complete, so reaching it means somebody added a stage and skipped
  // the palette.
  return STAGE_COLORS_ON_DARK[normalizeStage(stage)] || lightenToward('#6b7280', ON_DARK_LIFT);
}

// Legacy stage aliases — anything stored in the DB pre-migration maps forward
// so older rows continue to work. Used by `normalizeStage` below.
// Every pre-Lead-Center intake stage now lands on the first real stage
// of the funnel. A deal already stored as one of these was, by
// definition, a lead somebody had decided to work — Preliminary Title
// is where that work starts.
const LEGACY_MAP = {
  'Call In':       'Preliminary Title',
  'Contact Made':  'Preliminary Title',
  Identified:      'Preliminary Title',
  Contacted:       'Preliminary Title',
  'Inbound Call':  'Preliminary Title',
  'Call-In':       'Preliminary Title',
  CallIn:          'Preliminary Title',
  Interested:      'Pricing',
  'Offer Sent':    'Offer Made',
  Negotiating:     'Offer Made',
  Disposition:     'Processing',
  'Funds Disbursed': 'Funded',
};

/**
 * The canonical stage for a value we RECOGNISE — a current stage or a
 * known legacy alias — and null for anything else.
 *
 * normalizeStage answers 'Preliminary Title' for everything it does not
 * know, which is right for display: a card has to render somewhere. It
 * is wrong wherever a caller must tell "this is a real deal stored
 * under an old name" from "this is garbage", because the blanket
 * fallback makes corrupt data look like a legitimate deal. The
 * follow-up planner needs exactly that distinction: chase the first,
 * ignore the second.
 */
export function resolveKnownStage(raw) {
  // '' falls through both lookups to null on its own, so there is no
  // separate empty guard to get out of step with them.
  const key = String(raw ?? '').trim();
  if (DEAL_STAGES.includes(key)) return key;
  return LEGACY_MAP[key] || null;
}

export function normalizeStage(raw) {
  if (!raw) return 'Preliminary Title';
  if (DEAL_STAGES.includes(raw)) return raw;
  return LEGACY_MAP[raw] || 'Preliminary Title';
}

// Owned assets we are reselling. A closed deal listed as an asset for
// sale keeps its stage but renders in rose everywhere a stage color
// shows — map sections, tracker cards, badges — so "our inventory"
// reads at a glance apart from the buy-side pipeline.
export const ASSET_FOR_SALE_COLOR = '#e11d48';

export function isAssetForSale(deal) {
  return deal?.assetForSale === true;
}

// A deal placed with an investor.
//
// Orange-600 rather than the warning amber (#f59e0b) the health dot
// and the "Ready for Pricing" chip already use: it has to read as
// orange while still being a different mark from "something needs
// attention", and at this saturation the two do not trade places at a
// glance. It is also clear of the stage ramp (blue through green) and
// of asset-for-sale rose.
//
// Deliberately NOT a stage colour override like ASSET_FOR_SALE_COLOR:
// a placed deal still moves through the pipeline, so it keeps its
// stage colour and gains a stripe.
export const INVESTOR_PLACED_COLOR = '#ea580c';

// Stage accent with the asset-for-sale override — use this wherever a
// deal (not a bare stage) is being colored.
export function dealAccentColor(deal) {
  return isAssetForSale(deal) ? ASSET_FOR_SALE_COLOR : stageColor(deal?.stage);
}

export function stageColor(stage) {
  return STAGE_COLORS[normalizeStage(stage)] || '#6b7280';
}

// Lifecycle helpers — the `lifecycle` JSONB column on the deal is an object
// keyed by stage name with `{ completed: bool, completedAt: iso }`. When the
// user clicks stage N we backfill stages 1..N as completed with timestamps.
export function buildLifecycle(currentStage) {
  const s = normalizeStage(currentStage);
  const idx = LIFECYCLE_STAGES.indexOf(s);
  const now = new Date().toISOString();
  const out = {};
  LIFECYCLE_STAGES.forEach((stage, i) => {
    out[stage] = { completed: i <= idx, completedAt: i <= idx ? now : null };
  });
  return out;
}

// Given an existing lifecycle object + a target stage, return the new
// lifecycle with the target + every earlier stage marked complete, and every
// later stage cleared. Preserves existing completedAt timestamps where they
// still apply.
export function advanceLifecycle(lifecycle, targetStage) {
  const s = normalizeStage(targetStage);
  const targetIdx = LIFECYCLE_STAGES.indexOf(s);
  const now = new Date().toISOString();
  const out = { ...(lifecycle || {}) };
  LIFECYCLE_STAGES.forEach((stage, i) => {
    const existing = lifecycle?.[stage];
    if (i <= targetIdx) {
      out[stage] = existing?.completed
        ? existing
        : { completed: true, completedAt: now };
    } else {
      out[stage] = { completed: false, completedAt: null };
    }
  });
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// PASS REASONS — why a deal died
// ─────────────────────────────────────────────────────────────────────────────
// Passed is the largest single outcome in the funnel, and until now
// nothing recorded the cause, so the business had no way to answer
// which leads are worth buying more of. These codes are mirrored by the
// deals_pass_reason_check constraint in migration-161; adding one means
// touching both. Order is roughly most- to least-common so the picker
// puts the likely answer first.

export const DEAL_PASS_REASONS = [
  { key: 'price_gap',      label: 'Price gap',           hint: 'Owner wanted more than the deal supports' },
  { key: 'not_interested', label: 'Not interested',      hint: 'Owner declined outright' },
  { key: 'unreachable',    label: 'Could not reach',     hint: 'Never got the owner back on the phone' },
  { key: 'not_for_sale',   label: 'Not selling',         hint: 'Will not sell at any price right now' },
  { key: 'title_defect',   label: 'Title defect',        hint: 'Chain problems we could not clear' },
  { key: 'sold_elsewhere', label: 'Sold elsewhere',      hint: 'Another buyer got there first' },
  { key: 'too_small',      label: 'Too small',           hint: 'Below the economics we can work' },
  { key: 'other',          label: 'Other',               hint: 'Explain in the note' },
];

const PASS_REASON_KEYS = new Set(DEAL_PASS_REASONS.map((r) => r.key));

export function passReasonLabel(value) {
  const key = String(value || '').trim();
  return DEAL_PASS_REASONS.find((r) => r.key === key)?.label || null;
}
